// De instellingen voor Sentry in de widget, als gewone functies zonder afhankelijkheden.
//
// Waarom een eigen bestand. Op 15-09-2026 is Sentry voor het hele systeem gekoppeld aan één project,
// "jimani" (besluit Martijn). Toen bleek dat de widget op live zichzelf als "development" meldde:
// de omgeving kwam uit een bestand dat in de repo staat en bij het bouwen voor live gewoon meeging.
// En hij nam 10% van alle bezoeken op, terwijl er in het abonnement 50 opnames per maand zitten --
// dat quotum is dan in een paar uur op, en later in de maand is er geen opname meer bij een echte
// fout.
//
// Hier staan die keuzes als losse functies, zodat ze te toetsen zijn zonder een browser en zonder
// Vite. index.js geeft ze door aan Sentry.init.

const LIVE_ADRESSEN = ['widget.jimani.online'];

/** Een getal tussen 0 en 1, of de terugval. */
function deel(waarde, terugval) {
    const getal = typeof waarde === 'number' ? waarde : parseFloat(waarde);

    return Number.isFinite(getal) && getal >= 0 && getal <= 1 ? getal : terugval;
}

/**
 * De omgeving zoals hij in Sentry hoort te staan: live, dev of lokaal.
 *
 * Eerst het adres waar de widget op dat moment draait, want dat liegt niet. Pas als het adres niets
 * zegt -- de widget staat ingebed op de site van een klant -- de waarde waarmee hij gebouwd is.
 */
export function sentryOmgeving(hostnaam, bouwwaarde) {
    const adres = String(hostnaam || '').toLowerCase();

    if (adres === 'localhost' || adres.startsWith('127.') || adres === '[::1]' || adres.endsWith('.local')) {
        return 'lokaal';
    }

    if (LIVE_ADRESSEN.includes(adres)) {
        return 'live';
    }

    if (adres.endsWith('.jimani.online') && /dev/.test(adres)) {
        return 'dev';
    }

    const gebouwd = String(bouwwaarde || '').toLowerCase();

    if (gebouwd === 'production' || gebouwd === 'live') return 'live';
    if (gebouwd === 'development' || gebouwd === 'staging' || gebouwd === 'dev') return 'dev';
    if (gebouwd === 'local' || gebouwd === 'lokaal') return 'lokaal';

    return gebouwd || 'onbekend';
}

// AVG (wetgeving buiten de kassa, 14-10-2026): gastgegevens horen niet in Sentry. sendDefaultPii staat al uit, maar de
// standaard-kruimels van de browser (fetch/xhr/navigatie) nemen het hele adres mee, met de zoekreeks. Staat daar een
// e-mailadres, telefoonnummer of naam in, dan ging die zo naar Sentry (een Amerikaanse verwerker). Deze zeef haalt ze eruit.
const PERSOONLIJK = /mail|phone|telefoon|mobiel|mobile|naam|name|adres|address|postcode|zipcode|birth|geboorte/i;
const EMAIL = /[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}/g;
const EMAIL_GECODEERD = /[A-Za-z0-9._%+-]+%40[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}/gi;

/** Een adres zonder persoonsgegevens: waarden van persoonlijke zoekvelden en elk e-mailadres worden ***. */
export function poetsAdres(adres) {
    if (typeof adres !== 'string' || !adres) return adres;
    let uit = adres;
    const vraag = uit.indexOf('?');
    if (vraag >= 0) {
        const [zoek, anker] = uit.slice(vraag + 1).split('#');
        const delen = zoek.split('&').map((deel) => {
            const is = deel.indexOf('=');
            if (is < 0) return deel;
            const naam = deel.slice(0, is);
            return PERSOONLIJK.test(decodeURIComponentVeilig(naam)) ? naam + '=***' : deel;
        });
        uit = uit.slice(0, vraag + 1) + delen.join('&') + (anker !== undefined ? '#' + anker : '');
    }
    return uit.replace(EMAIL, '***').replace(EMAIL_GECODEERD, '***');
}

function decodeURIComponentVeilig(t) {
    try { return decodeURIComponent(t); } catch { return t; }
}

/** De kruimel zonder persoonsgegevens in het adres (fetch, xhr, navigatie) en in de tekst. */
export function poetsKruimel(kruimel) {
    if (!kruimel) return kruimel;
    const data = kruimel.data ? { ...kruimel.data } : kruimel.data;
    if (data) for (const veld of ['url', 'from', 'to']) if (typeof data[veld] === 'string') data[veld] = poetsAdres(data[veld]);
    const message = typeof kruimel.message === 'string' ? kruimel.message.replace(EMAIL, '***') : kruimel.message;
    return { ...kruimel, data, message };
}

/** De melding zonder persoonsgegevens in het adres van het verzoek. */
export function poetsMelding(melding) {
    if (melding?.request?.url) melding.request = { ...melding.request, url: poetsAdres(melding.request.url) };
    if (melding?.request?.query_string && typeof melding.request.query_string === 'string')
        melding.request.query_string = poetsAdres('?' + melding.request.query_string).slice(1);
    return melding;
}

/**
 * Alles wat er naar Sentry.init gaat, behalve de integraties.
 *
 * Lokaal meldt hij standaard niet, net als het dashboard en de api: een werkplek die proeffouten
 * naar het echte project stuurt maakt de meldingen van de zaken onleesbaar.
 */
export function sentryOpties({ dsn, hostnaam, bouwwaarde, versie, meetdeel, opnamedeel, foutopnamedeel, lokaalMelden }) {
    const omgeving = sentryOmgeving(hostnaam, bouwwaarde);
    const aan = Boolean(dsn) && (omgeving !== 'lokaal' || lokaalMelden === true);

    return {
        dsn: aan ? dsn : '',
        enabled: aan,
        environment: omgeving,
        release: versie ? 'jimani-widget@' + versie : undefined,

        // Het onderdeel als label: dashboard, api, kassa en widget melden allemaal in hetzelfde
        // project, en zo is er toch per onderdeel te filteren.
        initialScope: { tags: { onderdeel: 'widget' } },

        // 10% van de bezoeken gemeten. Er zitten 5 miljoen spans in het abonnement.
        tracesSampleRate: deel(meetdeel, 0.1),

        // Geen gewone opnames: er zitten er 50 per maand in het abonnement. Wel bij elke fout, en
        // dan heb je ze precies voor de keren dat het ertoe doet.
        replaysSessionSampleRate: deel(opnamedeel, 0),
        replaysOnErrorSampleRate: deel(foutopnamedeel, 1),

        // Geen gastgegevens in de kruimels en het verzoekadres (AVG, 14-10-2026).
        beforeBreadcrumb: (kruimel) => poetsKruimel(kruimel),
        beforeSend: (melding) => poetsMelding(melding),
    };
}
